import {EventStatus} from "../../../../shared/enums/eventStatus";
import {EventRecurrenceDetails} from "./eventRecurrenceDetails";
import {RecurringStatus} from "../../../../shared/enums/RecurringStatus";

// Price model in BFF
interface PriceModel {
  amount?: number;
  costAllocation?: string;
  currency?: string;
  formatted?: string;
}

/**
 * This is not a strong representation of an event in the system
 * Instead it is a mapping between the event from CMS, and the definition required for full calendar.
 *
 * You may need to add any additional properties into @see {extendedProps}
 */
export default class EventSummaryItem {
  id: string;
  start: Date;
  end: Date;
  title: string;
  location: string;
  timeZone: string;
  allDay?: boolean;
  className: string;
  isFullyBooked: boolean;
  eventRecurrenceDetails: EventRecurrenceDetails;
  recurringStatus?: RecurringStatus;
  releaseDate?: Date;
  description?: string;
  category?: string;
  occurrenceDate?: Date;
  price?: PriceModel;

  // A "bucket" for any extra properties not exposed on the Full Calendar Api
  // This is necessary because we give FC a list of EventSummaryItem,
  // and when rendering events we get EventContentArg
  extendedProps: {
    isBookableEvent: boolean;
    capacityRemaining?: number;
    reservedCapacity?: number;
    reservedCapacityRemaining?: number;
    isFeaturedEvent: boolean;
    capacity?: number;
    isMultiVillageEvent?: boolean;
    eventStatus: EventStatus;
    statusChangeReason?: string;
    eventRecurrenceDetails: EventRecurrenceDetails;
    recurringStatus?: RecurringStatus;
    eventStartDateTimeOffSet?: string;
    releaseDate?: Date;
    description?: string;
    bookingDeadline?: string;
    totalStaffAssistedBookings?: number;
    totalBookings?: number;
    totalWaitlistedBookings?: number;
    totalCancelledBookings?: number;
  };

  constructor(
    id: string,
    start: Date,
    end: Date,
    title: string,
    location: string,
    timeZone: string,
    allDay?: boolean,
    isBookableEvent?: boolean,
    capacityRemaining?: number,
    reservedCapacityRemaining?: number,
    isFeaturedEvent?: boolean,
    capacity?: number,
    isMultiVillageEvent?: boolean,
    eventStatus?: EventStatus,
    eventRecurrenceDetails?: EventRecurrenceDetails,
    recurringStatus?: RecurringStatus,
    eventStartDateTimeOffSet?: string,
    releaseDate?: Date,
    description?: string,
    category?: string,
    occurrenceDate?: Date,
    price?: PriceModel,
    bookingDeadline?: string,
    totalStaffAssistedBookings?: number,
    totalBookings?: number,
    totalWaitlistedBookings?: number,
    totalCancelledBookings?: number,
    statusChangeReason?: string,
    reservedCapacity?: number
  ) {
    this.id = id;
    this.start = start;
    this.end = end;
    this.title = title;
    this.location = location;
    this.allDay = false; // todo: remove all day everywhere
    this.extendedProps = {
      isBookableEvent: isBookableEvent ?? false,
      capacityRemaining: capacityRemaining,
      reservedCapacity: reservedCapacity,
      reservedCapacityRemaining: reservedCapacityRemaining,
      isFeaturedEvent: isFeaturedEvent ?? false,
      capacity: capacity,
      isMultiVillageEvent: isMultiVillageEvent ?? false,
      eventStatus: eventStatus ?? EventStatus.Published,
      statusChangeReason: statusChangeReason,
      eventRecurrenceDetails:
        eventRecurrenceDetails ?? new EventRecurrenceDetails(timeZone),
      recurringStatus: recurringStatus,
      eventStartDateTimeOffSet: eventStartDateTimeOffSet,
      releaseDate: releaseDate,
      bookingDeadline: bookingDeadline,
      totalStaffAssistedBookings: totalStaffAssistedBookings,
      totalBookings: totalBookings,
      totalWaitlistedBookings: totalWaitlistedBookings,
      totalCancelledBookings: totalCancelledBookings
    };
    this.className = "bg-calendar";
    this.isFullyBooked = capacityRemaining === 0;
    this.timeZone = timeZone;
    this.eventRecurrenceDetails =
      eventRecurrenceDetails ?? new EventRecurrenceDetails(timeZone);
    this.recurringStatus = recurringStatus;
    this.releaseDate = releaseDate;
    this.description = description;
    this.category = category;
    this.occurrenceDate = occurrenceDate;
    this.price = price;
  }
}
